"use client";

import { useState } from "react";
import Link from "next/link";
import type { Country, Locale } from "@/lib/i18n";
import { localizeHref } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";
import { formatPrice } from "@/lib/pricing";

export type PlanProductIds = {
  monthly: string;
  yearly: string;
};

export type Plan = {
  name: string;
  price: string;
  cadence: string;
  desc: string;
  features: string[];
  cta: string;
  /** Service catalog ids per billing cadence (panel `Service.id`). */
  productId: PlanProductIds | "";
  popular: boolean;
};

const panelUrl = (process.env.NEXT_PUBLIC_PANEL_URL ?? "").replace(/\/$/, "");

function billingParam(billing: "monthly" | "yearly"): "m" | "a" {
  return billing === "yearly" ? "a" : "m";
}

function resolveProductId(billing: "monthly" | "yearly", productId: PlanProductIds | ""): string {
  if (!productId || typeof productId !== "object") return "";
  const id = billing === "yearly" ? productId.yearly : productId.monthly;
  return id.trim();
}

function normalizeProductQuery(productId: string): string {
  const id = productId.trim();
  if (!id) return "";
  if (id.includes("=")) return id;
  return `product_id=${encodeURIComponent(id)}`;
}

function planUsesOnboard(plan: Plan): boolean {
  return plan.cta !== "Talk to Sales" && plan.cta !== "Parler aux ventes";
}

function buildOnboardHref(billing: "monthly" | "yearly", plan: Plan): string | null {
  if (!panelUrl || !planUsesOnboard(plan)) return null;
  const productId = resolveProductId(billing, plan.productId);
  if (!productId) return null;
  const billingQ = `billing=${billingParam(billing)}`;
  const productQ = normalizeProductQuery(productId);
  const qs = productQ ? `${billingQ}&${productQ}` : billingQ;
  return `${panelUrl}/hosting-control-panel/onboard?${qs}`;
}

function cn(...classes: Array<string | false | undefined | null>) {
  return classes.filter(Boolean).join(" ");
}

function BillingToggle({
  billing,
  onToggle,
  locale,
}: {
  billing: "monthly" | "yearly";
  onToggle: () => void;
  locale: Locale;
}) {
  const m = getMessages(locale);
  return (
    <div className="mb-6 flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-slate-600 dark:text-white/70">{m.hosting.billing}</span>
      <button
        type="button"
        role="switch"
        aria-checked={billing === "yearly"}
        aria-label="Toggle monthly or yearly billing"
        onClick={onToggle}
        className="relative flex h-9 w-40 items-center rounded-full bg-white/50 ring-1 ring-white/40 backdrop-blur-xl dark:bg-white/10 dark:ring-white/20"
      >
        <span
          className={cn(
            "absolute left-1 top-1 flex h-7 w-20 items-center justify-center rounded-full bg-[#27598e]/90 text-xs font-semibold text-white shadow transition-transform backdrop-blur-sm dark:bg-white dark:text-black",
            billing === "yearly" && "translate-x-[4.5rem]"
          )}
        >
          {billing === "monthly" ? m.hosting.monthly : m.hosting.yearly}
        </span>
      </button>
      {billing === "yearly" && (
        <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300">
          {m.hosting.save20}
        </span>
      )}
    </div>
  );
}

function PlanCard({
  plan,
  billing,
  country,
  locale,
}: {
  plan: Plan;
  billing: "monthly" | "yearly";
  country: Country;
  locale: Locale;
}) {
  const m = getMessages(locale);
  const yearly = billing === "yearly";
  const displayPrice = formatPrice(plan.price, { country, yearly });
  const onboardHref = buildOnboardHref(billing, plan);
  const ctaHref = onboardHref ?? localizeHref("/contact", { locale, country });
  const ctaExternal = onboardHref !== null;
  const ctaClassName = cn(
    "mt-6 inline-flex w-full items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold transition-colors duration-200",
    plan.popular
      ? "bg-[#27598e]/90 text-white backdrop-blur-sm hover:bg-[#27598e] dark:bg-white dark:text-black dark:hover:bg-white/90"
      : "bg-white/50 text-slate-700 ring-1 ring-white/40 backdrop-blur-xl hover:bg-white/60 dark:bg-white/10 dark:text-white dark:ring-white/20 dark:hover:bg-white/15"
  );
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[2rem] p-6 ring-1 backdrop-blur-2xl transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-300/25 hover:ring-white/50 dark:hover:shadow-slate-900/30 dark:hover:ring-white/40",
        plan.popular ? "bg-white/50 ring-white/40 dark:bg-white/14 dark:ring-white/30" : "bg-white/40 ring-white/30 dark:bg-white/10 dark:ring-white/20"
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent dark:from-white/15 dark:via-transparent" />
      {plan.popular && (
        <div className="absolute right-5 top-5 rounded-full bg-[#27598e]/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm dark:bg-white dark:text-black">
          {m.hosting.popular}
        </div>
      )}
      <div className="relative">
        <div className="text-lg font-semibold text-slate-900 dark:text-white">{plan.name}</div>
        <div className="mt-2 flex flex-wrap items-baseline gap-1">
          <div className="text-3xl font-semibold text-slate-900 dark:text-white">{displayPrice}</div>
          <div className="text-sm text-slate-600 dark:text-white/70">{plan.cadence}</div>
          {yearly && (
            <span className="ml-1 text-xs text-slate-500 dark:text-white/55">{m.hosting.billedYearly}</span>
          )}
        </div>
        <div className="mt-2 text-sm text-slate-600 dark:text-white/70">{plan.desc}</div>
        <ul className="mt-5 space-y-2 text-sm text-slate-600 dark:text-white/80">
          {plan.features.map((f) => (
            <li key={f} className="flex gap-2">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-500 dark:bg-white/70" />
              <span className="text-slate-600 dark:text-white/75">{f}</span>
            </li>
          ))}
        </ul>
        {ctaExternal ? (
          <a href={ctaHref} className={ctaClassName}>
            {plan.cta}
          </a>
        ) : (
          <Link href={ctaHref} className={ctaClassName}>
            {plan.cta}
          </Link>
        )}
        <div className="mt-3 text-center text-xs text-slate-500 dark:text-white/55">
          {m.hosting.cancelAnytime}
        </div>
      </div>
    </div>
  );
}

export function HostingPricing({
  vpsPlans,
  sharedPlans,
  country = "US",
  locale = "en",
}: {
  vpsPlans: Plan[];
  sharedPlans: Plan[];
  country?: Country;
  locale?: Locale;
}) {
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const m = getMessages(locale);

  return (
    <>
      <section id="vps" className="mt-20">
        <h3 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-white">{m.hosting.vpsWhatTitle}</h3>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-white/75">
          {m.hosting.vpsWhatBody}
        </p>
        <h2 className="mt-8 text-2xl font-semibold tracking-tight dark:text-white">{m.hosting.vpsTitle}</h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-white/70">
          {m.hosting.vpsIntro}
        </p>
        <BillingToggle billing={billing} onToggle={() => setBilling((b) => (b === "monthly" ? "yearly" : "monthly"))} locale={locale} />
        <div className="grid gap-4 md:grid-cols-3">
          {vpsPlans.map((p) => (
            <PlanCard key={p.name} plan={p} billing={billing} country={country} locale={locale} />
          ))}
        </div>
      </section>

      <section id="shared" className="mt-20">
        <h2 className="text-2xl font-semibold tracking-tight dark:text-white">{m.hosting.sharedTitle}</h2>
        <p className="mt-2 max-w-2xl rounded-2xl bg-white/45 px-4 py-3 text-sm text-slate-600 ring-1 ring-white/35 backdrop-blur-xl dark:bg-white/10 dark:text-white/70 dark:ring-white/15">
          {m.hosting.sharedIntro}
        </p>
        <BillingToggle billing={billing} onToggle={() => setBilling((b) => (b === "monthly" ? "yearly" : "monthly"))} locale={locale} />
        <div className="grid gap-4 md:grid-cols-3">
          {sharedPlans.map((p) => (
            <PlanCard key={p.name} plan={p} billing={billing} country={country} locale={locale} />
          ))}
        </div>
      </section>
    </>
  );
}
